import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form as FinalForm } from 'react-final-form';

import Modal from '../Modal/Modal';
import { bool, func, string } from 'prop-types';
import { propTypes } from '../../util/types';
import { Button, FieldTextInput, ExternalLink } from '../../components';
import * as validators from '../../util/validators';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import config from '../../config';

import css from './NewAgreementPopup.module.css';

const NewAgreementPopup = props => {
  const {
    isOpen,
    onClose,
    onManageDisableScrolling,
    className,
    currentUser,
    currentUserListing
  } = props;

  const [saveInProgress, setSaveInProgress] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const intl = useIntl();
  const {
    id: { uuid: currentListingId } = {},
    attributes: { title: currentListingTitle } = {},
  } = currentUserListing || {};

  const user = currentUser;
  const { businessName, businessNumber } = user?.attributes?.profile?.publicData || {};

  // Check if user has business data in their profile
  const hasBusinessDataInProfile = !!(businessName && businessNumber);

  const handleSubmit = async (values) => {
    setSaveInProgress(true);
    setSaveError(null);

    try {
      await dispatch(updateProfile({
        publicData: {
          businessName: values.businessName?.trim(),
          businessNumber: values.businessNumber?.trim(),
        },
      }));
      
      // Success - the Redux store will be updated automatically
      setSaveInProgress(false);
    } catch (error) {
      console.error('Error saving business details:', error);
      setSaveError(error);
      setSaveInProgress(false);
    }
  };

  const handleViewAndSign = () => {
    onClose();
    if (currentListingId && currentListingTitle) {
      history.push(`/l/${currentListingTitle}/${currentListingId}/edit/agreement`);
    }
  };

  return (
    <Modal
      id="NewAgreementPopup.newAgreementModal"
      className={className}
      containerClassName={css.modalContainer}
      contentClassName={css.content}
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling}
      usePortal
      closeButtonMessage={<FormattedMessage id="NewAgreementPopup.closeButton" />}
    >
      <div className={css.modalContent}>
        <h2 className={css.title}>
          <FormattedMessage id="NewAgreementPopup.title" />
        </h2>

        <p className={css.description}>
          <FormattedMessage
            id="NewAgreementPopup.description1"
            values={{
              announcementLink: (
                <ExternalLink href={process.env.REACT_APP_ANNOUNCEMENT_LINK} className={css.externalLink}>
                  <FormattedMessage id="NewAgreementPopup.clickHere" />
                </ExternalLink>
              ),
              coachFaqLink: (
                <ExternalLink href={process.env.REACT_APP_COACH_FAQ_LINK} className={css.externalLink}>
                  <FormattedMessage id="NewAgreementPopup.clickHere" />
                </ExternalLink>
              )
            }}
          />
        </p>
        <p className={css.description}>
          <FormattedMessage
            id="NewAgreementPopup.description2"
          />
        </p>

        <p className={css.contractInfo}>
          <FormattedMessage id="NewAgreementPopup.contractInfo" />
        </p>

        <FinalForm
          onSubmit={handleSubmit}
          initialValues={{
            businessName: businessName || '',
            businessNumber: businessNumber || '',
          }}
          render={({ handleSubmit, values }) => {
            const businessNumberValidator = validators.validNumericAndLetters(
              <FormattedMessage id="NewAgreementPopup.businessNumberInvalid" />
            );

            const hasBusinessFields = values.businessName || values.businessNumber;


            return (
              <form onSubmit={handleSubmit} className={css.form}>
                <div className={css.fields}>
                  <div className={css.fieldColumn}>
                    <div className={css.fieldWrapper}>
                      <label htmlFor="businessName">
                        <FormattedMessage id="NewAgreementPopup.businessNameLabel" />
                      </label>
                      <FieldTextInput
                        id="businessName"
                        name="businessName"
                        type="text"
                        placeholder={intl.formatMessage({
                          id: 'NewAgreementPopup.businessNamePlaceholder',
                        })}
                      />
                    </div>
                    <div className={css.fieldWrapper}>
                      <label htmlFor="businessNumber">
                        <FormattedMessage id="NewAgreementPopup.businessNumberLabel" />
                      </label>
                      <FieldTextInput
                        id="businessNumber"
                        name="businessNumber"
                        type="text"
                        placeholder={intl.formatMessage({
                          id: 'NewAgreementPopup.businessNumberPlaceholder',
                        })}
                        validate={businessNumberValidator}
                      />
                    </div>
                  </div>
                  <div className={css.buttonColumn}>
                    <Button
                      type="submit"
                      inProgress={saveInProgress}
                      disabled={!hasBusinessFields || saveInProgress}
                      className={css.saveButton}
                    >
                      <FormattedMessage id="NewAgreementPopup.saveButton" />
                    </Button>
                  </div>
                </div>

                {saveError && (
                  <div className={css.error}>
                    <FormattedMessage id="NewAgreementPopup.saveError" />
                  </div>
                )}

                <div className={css.actionButtonContainer}>
                  <Button
                    type="button"
                    onClick={handleViewAndSign}
                    disabled={saveInProgress}
                    className={css.viewAgreementButton}
                  >
                    <FormattedMessage id="NewAgreementPopup.viewAndSignButton" />
                  </Button>
                </div>

                <p>
                  <FormattedMessage
                    id="NewAgreementPopup.contactInfo"
                    values={{
                      email: (
                        <a href={`mailto:${config.supportEmail}`} className={css.emailLink}>
                          {config.supportEmail}
                        </a>
                      ),
                    }}
                  />
                </p>
              </form>
            );
          }}
        />
      </div>
    </Modal>
  );
};

NewAgreementPopup.propTypes = {
  isOpen: bool,
  onClose: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  className: string,
  currentUser: propTypes.currentUser,
};

NewAgreementPopup.defaultProps = {
  isOpen: false,
  className: null,
  currentUser: null,
};

export default NewAgreementPopup;
